import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { MapPin, X } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api.js';
import { Card, SaveButton, isDirty, LoadingPanel } from './_shared.jsx';

export default function GeoTab() {
  const [allStates, setAllStates] = useState([]);
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet('/settings/geo-restrictions').then((r) => {
      if (r?.ok) {
        setAllStates(r.allStates);
        setLoaded(r.unlicensedStates);
        setDraft(r.unlicensedStates);
      }
    }).catch((e) => toast.error(e.message || 'Failed to load geo restrictions'));
  }, []);

  function toggle(s) {
    if (draft.includes(s)) setDraft(draft.filter((x) => x !== s));
    else setDraft([...draft, s].sort());
  }

  async function save() {
    const prev = loaded;
    setBusy(true);
    setLoaded(draft);
    try {
      const r = await apiPut('/settings/geo-restrictions', { unlicensedStates: draft });
      if (!r?.ok) throw new Error('Save failed');
      setLoaded(r.unlicensedStates);
      setDraft(r.unlicensedStates);
      toast.success('Geo restrictions saved');
    } catch (e) {
      setLoaded(prev);
      setDraft(prev);
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  const blockedSet = useMemo(() => new Set(draft || []), [draft]);
  if (!draft) return <LoadingPanel label="Loading geo-restrictions…" />;
  const dirty = isDirty(draft, loaded);

  return (
    <div className="space-y-4">
      <Card
        title="Geo-Restrictions (Unlicensed States)"
        description="Inbound leads from these US states will be rejected at intake. Use this when your producers are not licensed in a state."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} label="Save Block List" />}
      >
        {draft.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <MapPin className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <span className="text-xs text-amber-200 mr-1">Blocked:</span>
            {draft.map((s) => (
              <button key={s} onClick={() => toggle(s)} className="pill bg-amber-500/20 text-amber-200 border-amber-500/40 inline-flex items-center gap-1 hover:bg-amber-500/30">
                {s} <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5">
          {allStates.map((s) => {
            const blocked = blockedSet.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggle(s)}
                className={`px-2 py-1.5 rounded text-xs font-medium border transition ${
                  blocked
                    ? 'bg-amber-500/20 border-amber-500/40 text-amber-200'
                    : 'bg-civix-panelLight border-civix-border/60 text-white/70 hover:border-civix-purple/60'
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
        <div className="text-xs text-white/40 pt-2 border-t border-civix-border/40">
          Click a state to toggle it. Default block: <code className="font-mono">NY</code>.
          Tenant lead intake honors this list — see <code>tenant.unlicensedStates</code> on the server.
        </div>
      </Card>
    </div>
  );
}
